import { useBoolean } from "@fluentui/react-hooks";
import { useCallback, useEffect, useState } from "react";
import { logEvent } from "../../../helper/AppInsightsHelper";
import { PromptLibraryItem } from "../../../interfaces/slices.interfaces";
import {
  useBulkUpdatePromptPlaygroundUserSessionsMutation,
  useDeletePromptPlaygroundUserSessionMutation,
  useGetPromptPlaygroundUserSessionsQuery,
  useUpdatePromptPlaygroundUserSessionMutation
} from "../../../slices/AIHub.slice";
import { scheduleFrequency, timeoutDelays } from "../AIPlayground.constants";
import {
  BulkUpdateStatus,
  DeleteStatus,
  SavedStatus,
  SessionData,
  UseSessionsProps,
  UseSessionsReturn
} from "../Interfaces/Sessions.interfaces";

export const useSessions = (props: UseSessionsProps): UseSessionsReturn => {
  const { data } = useGetPromptPlaygroundUserSessionsQuery();
  const [rows, setRows] = useState<SessionData[]>([]);
  const [enablePanel, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [selectedRowData, setSelectedRowData] = useState<SessionData | undefined>(undefined);
  const [isSessionDataEditable, setIsSessionDataEditable] = useState<boolean>(false);
  const [savedStatus, setSavedStatus] = useState<SavedStatus>({
    isSaved: false,
    isSaving: false
  });
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatus>({
    deleted: false,
    isDeleting: false
  });
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState<BulkUpdateStatus>({
    isBulkUpdating: false,
    isBulkUpdated: false
  });
  const [selectedForDeletion, setSelectedForDeletion] = useState<string[]>([]);
  const [isSessionModified, setIsSessionModified] = useState<boolean>(false);

  const [updateSession] = useUpdatePromptPlaygroundUserSessionMutation();
  const [deleteSession] = useDeletePromptPlaygroundUserSessionMutation();
  const [bulkUpdateSession] = useBulkUpdatePromptPlaygroundUserSessionsMutation();

  useEffect(() => {
    const updatedRowsWithFrequency = (data ?? []).map((row) => ({
      ...row,
      scheduleFrequency: row.scheduleFrequency === "" ? "N/A" : row.scheduleFrequency
    }));
    setRows(updatedRowsWithFrequency);
  }, [data]);

  const handleRunClick = (action: "run" | "open") => {
    setIsSessionDataEditable(false); // Reset edit mode when running or opening a session
    dismissPanel();
    const data: PromptLibraryItem = {
      id: 0,
      title: selectedRowData?.title ?? "",
      description: selectedRowData?.description ?? "",
      query: selectedRowData?.query ?? "",
      prompt: selectedRowData?.prompt ?? "",
      tags: ""
    };
    setTimeout(() => props.onRunPrompt?.(data, action), timeoutDelays.RUN_PROMPT);
  };

  const handleOnChangeSessionData = (field: keyof SessionData, value: string | boolean) => {
    if (!selectedRowData) return;
    if (field === "isActiveSchedule") {
      if (!value) {
        setSelectedRowData((prev) => (prev ? { ...prev, isActiveSchedule: false, scheduleFrequency: "" } : undefined));
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === selectedRowData.id ? { ...row, isActiveSchedule: false, scheduleFrequency: "" } : row
          )
        );
      } else {
        setSelectedRowData((prev) =>
          prev ? { ...prev, isActiveSchedule: true, scheduleFrequency: "monthly" } : undefined
        );
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === selectedRowData.id ? { ...row, isActiveSchedule: true, scheduleFrequency: "monthly" } : row
          )
        );
      }
    } else {
      setSelectedRowData((prev) => (prev ? { ...prev, [field]: value } : undefined));
      setRows((prevRows) => prevRows.map((row) => (row.id === selectedRowData.id ? { ...row, [field]: value } : row)));
    }
  };

  const handleSaveButtonClick = async () => {
    if (!selectedRowData) {
      console.warn("No session data to save.");
      return;
    }
    try {
      setSavedStatus({ isSaving: true, isSaved: false }); // Set saving state
      await updateSession({
        id: selectedRowData.id,
        title: selectedRowData.title,
        description: selectedRowData.description,
        query: selectedRowData.query,
        prompt: selectedRowData.prompt,
        isActiveSchedule: selectedRowData.isActiveSchedule,
        scheduleFrequency: selectedRowData.scheduleFrequency === "N/A" ? "" : selectedRowData.scheduleFrequency,
        isPublic: true // set to true for now, can be changed later
      });
      setSavedStatus({ isSaving: false, isSaved: true }); // Set saved state after a delay
      setTimeout(() => {
        setSavedStatus({ isSaving: false, isSaved: false }); // Set saved state after a delay
        setIsSessionDataEditable(false); // Reset edit mode after saving
        setSelectedRowData(undefined); // Clear selected row data after saving
        dismissPanel(); // Dismiss the panel after saving
      }, timeoutDelays.SAVE_RESET);
    } catch (error) {
      console.error("Error updating session:", error);
    }
  };

  const handleDeleteButtonClick = async () => {
    if (!selectedRowData) {
      console.warn("No session data to delete.");
      return;
    }
    try {
      setDeleteStatus({ isDeleting: true, deleted: false }); // Set deleting state
      const updatedRows = rows.filter((row) => row.id !== selectedRowData.id); // Create filtered rows
      setRows(updatedRows); // Remove the row from the table
      setDeleteStatus({ isDeleting: false, deleted: true }); // Set deleted state after a delay
      await deleteSession({
        id: selectedRowData.id,
        title: selectedRowData.title,
        description: selectedRowData.description,
        query: selectedRowData.query,
        prompt: selectedRowData.prompt,
        isActiveSchedule: selectedRowData.isActiveSchedule,
        scheduleFrequency: selectedRowData.scheduleFrequency === "N/A" ? "" : selectedRowData.scheduleFrequency,
        isPublic: true // set to true for now, can be changed later
      });
      setTimeout(() => {
        setDeleteStatus({ isDeleting: false, deleted: false }); // Reset delete state after a delay
        setIsSessionDataEditable(false); // Reset edit mode after deleting
        setSelectedRowData(undefined); // Clear selected row data after deleting
        dismissPanel(); // Dismiss the panel after deleting
      }, timeoutDelays.DELETE_RESET);
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };

  const normalizeScheduleFrequency = (shouldSetToNA: boolean, sessions: SessionData[]): SessionData[] => {
    return sessions.map((session) => {
      const currentFrequency = session.scheduleFrequency;

      if (shouldSetToNA) {
        return {
          ...session,
          scheduleFrequency:
            currentFrequency === scheduleFrequency.NOT_APPLICABLE ? scheduleFrequency.EMPTY : currentFrequency
        };
      } else {
        return {
          ...session,
          scheduleFrequency:
            currentFrequency === scheduleFrequency.EMPTY ? scheduleFrequency.NOT_APPLICABLE : currentFrequency
        };
      }
    });
  };

  const handleBulkUpdate = async () => {
    if (rows && rows.length === 0) return;
    const updatedRowsWithFrequency = normalizeScheduleFrequency(true, rows); // Update the rows with frequency
    try {
      setBulkUpdateStatus({ isBulkUpdating: true, isBulkUpdated: false }); // Set bulk updating state
      const updatedRows = updatedRowsWithFrequency
        ? updatedRowsWithFrequency.filter((row) => !selectedForDeletion.includes(row.id))
        : [];
      logEvent("Bulk Update Sessions button clicked", "AI Hub - KQL Mode", "useSessions", updatedRows.length);
      await bulkUpdateSession(updatedRows);
      setBulkUpdateStatus({ isBulkUpdating: false, isBulkUpdated: true }); // Set bulk updated state
      setTimeout(() => {
        setBulkUpdateStatus({ isBulkUpdating: false, isBulkUpdated: false }); // Reset bulk update state after a delay
      }, timeoutDelays.BULK_UPDATE_RESET);
      const updatedFrequency = normalizeScheduleFrequency(false, updatedRows);
      setRows(updatedFrequency);
      setSelectedForDeletion([]); // Clear the selectedForDeletion array
    } catch (error) {
      console.error("Error bulk updating sessions:", error);
    }
  };

  const handleDismissPanel = () => {
    setIsSessionDataEditable(false); // Reset edit mode when dismissing the panel
    setSelectedRowData(undefined); // Clear selected row data
    dismissPanel();
  };

  const handleOpenSessionsPanel = (rowId: string) => {
    setSelectedRowData(rows.find((row) => row.id === rowId)); // Find the row data based on the rowId
    openPanel();
    setIsSessionDataEditable(false); // Reset edit mode when opening a session
  };

  const handleFieldChange = useCallback(
    (field: string, value: string | boolean, rowId: string) => {
      setIsSessionModified(true);
      if (field === "delete") {
        setSelectedForDeletion((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
      } else if (field === "isActiveSchedule") {
        // if the value is "active", set isActiveSchedule to true and scheduleFrequency to "monthly"
        if (!value) {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === rowId ? { ...row, isActiveSchedule: false, scheduleFrequency: "N/A" } : row
            )
          );
        } else {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === rowId ? { ...row, isActiveSchedule: true, scheduleFrequency: "monthly" } : row
            )
          );
        }
      } else {
        setRows((prevRows) => prevRows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))); // Update the specific field
      }
    },
    [setRows]
  );

  return {
    rows,
    enablePanel,
    selectedRowData,
    isSessionDataEditable,
    setIsSessionDataEditable,
    isSessionModified,
    setIsSessionModified,
    savedStatus,
    deleteStatus,
    bulkUpdateStatus,
    selectedForDeletion,
    setSelectedForDeletion,
    handleRunClick,
    handleOnChangeSessionData,
    handleSaveButtonClick,
    handleDeleteButtonClick,
    handleBulkUpdate,
    handleDismissPanel,
    handleOpenSessionsPanel,
    handleFieldChange
  };
};
